import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { StartPaymentService } from '../start-payment.service';

export type StartFeePaymentMethod = 'card' | 'transfer' | 'credit';

export type StartPaymentChoiceDialogData = {
  caseId: string;
  canPayWithCredit: boolean;
};

@Component({
  selector: 'payee-start-payment-choice-dialog',
  templateUrl: './start-payment-choice-dialog.component.html',
  styleUrls: ['./start-payment-choice-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    PayeeButtonComponent,
    TranslateModule,
    FormsModule,
    MatRadioGroup,
    MatRadioButton,
  ],
})
export class StartPaymentChoiceDialogComponent {
  private dialogRef = inject<MatDialogRef<StartPaymentChoiceDialogComponent>>(MatDialogRef);
  private startPayment = inject(StartPaymentService);
  readonly data = inject<StartPaymentChoiceDialogData>(MAT_DIALOG_DATA);

  paymentMethod: StartFeePaymentMethod = 'card';
  readonly loading = signal(false);

  async submit(): Promise<void> {
    if (this.loading()) {
      return;
    }

    this.loading.set(true);
    try {
      if (this.paymentMethod === 'card') {
        await this.startPayment.payWithCard(this.data.caseId);
        return;
      }

      if (this.paymentMethod === 'transfer') {
        const instructions = await this.startPayment.requestProForma(this.data.caseId);
        if (!instructions) {
          this.loading.set(false);
          return;
        }

        this.dialogRef.close();
        this.startPayment.showTransferInstructions(instructions);
        return;
      }

      const started = await this.startPayment.payWithCredit(this.data.caseId);
      if (!started) {
        this.loading.set(false);
        return;
      }

      this.dialogRef.close();
    } catch (error: unknown) {
      this.startPayment.showError(error);
      this.loading.set(false);
    }
  }
}
